import type { WebPageAttributes } from '~/types/schema-org'
import { logoImages } from '~/models/images'

type SchemaOrgTranslations = Pick<ReturnType<typeof useI18n>, 't' | 'te'>

/**
 * @param webPage
 * @param baseKey Fill in if translations string do not yet provide a base key
 * @param translations Fill in if the result of this function will be used as a computed value
 */
export function getSchemaOrg(
  webPage: WebPageAttributes,
  baseKey?: string,
  translations?: SchemaOrgTranslations
): Record<string, unknown>[] {
  const { t, te } = translations ?? useI18n(baseKey)
  const route = useRoute()

  const thuisgekooktName = useLocaleStore().localeIsDutch
    ? 'Stichting Thuisgekookt'
    : 'The Thuisgekookt Foundation'
  const thuisgekooktUrl = 'https://thuisgekookt.nl'
  const title = te(webPage.title) ? t(webPage.title) : webPage.title

  const listElements = (webPage.breadcrumbs ?? []).map((breadcrumb) => {
    return {
      name: te(breadcrumb.name) ? t(breadcrumb.name) : breadcrumb.name,
      item: breadcrumb.item
    }
  })

  const schemas: Record<string, unknown>[] = [
    defineOrganization({
      name: thuisgekooktName,
      logo: logoImages.thuisgekookt.src,
      sameAs: [
        'https://www.facebook.com/ThuisgekooktNL/',
        'https://www.instagram.com/thuisgekookt/',
        'https://x.com/thuisgekookt',
        'https://nl.pinterest.com/infothuisgekookt/',
        'https://www.linkedin.com/company/thuisgekookt',
        'https://www.youtube.com/channel/UCYzMazH-n0hx2GObF5jD2VQ'
      ]
    }),

    defineWebPage({
      '@type': webPage.type ?? 'WebPage',
      name: title,
      url: `${thuisgekooktUrl}${route.path}`
    }),

    defineBreadcrumb({
      itemListElement: [{ name: thuisgekooktName, item: '/' }, ...listElements]
    })
  ]

  if (webPage.howTo) {
    schemas.push(
      defineHowTo({
        name: te(webPage.howTo.name) ? t(webPage.howTo.name) : webPage.howTo.name,
        step: webPage.howTo.step
      })
    )
  }

  if (webPage.article) {
    schemas.push(
      defineArticle({
        headline: title,
        description: webPage.article.description,
        image: webPage.article.image,
        datePublished: webPage.article.datePublished,
        author: {
          name: thuisgekooktName,
          url: thuisgekooktUrl
        }
      })
    )
  }

  if (webPage.jobPosting) {
    schemas.push(
      defineJobPosting({
        title: title,
        description: webPage.jobPosting.description,
        datePosted: webPage.jobPosting.datePosted,
        employmentType: webPage.jobPosting.employmentType,
        validThrough: dayjsUTC().add(1, 'year').format('YYYY-MM-DD'),
        baseSalary: {
          currency: 'EUR',
          value: {
            value: webPage.jobPosting.baseSalary,
            unitText: 'MONTH'
          }
        },
        hiringOrganization: {
          name: thuisgekooktName
        },
        jobLocation: {
          name: 'Office',
          address: {
            streetAddress: 'Ravellaan 13',
            addressLocality: 'Welgelegen, Den Hommel',
            postalCode: '3533 JE',
            addressCountry: 'Nederland',
            addressRegion: 'Utrecht'
          }
        }
      })
    )
  }

  if (webPage.recipe) {
    schemas.push(
      defineRecipe({
        name: title,
        image: webPage.recipe.image,
        recipeInstructions: webPage.recipe.instructions,
        recipeIngredient: webPage.recipe.ingredients,
        recipeCategory: webPage.recipe.category,
        recipeCuisine: webPage.recipe.cuisine,
        prepTime: webPage.recipe.prepTime,
        keywords: webPage.recipe.keywords,
        cookTime: webPage.recipe.cookTime,
        recipeYield: webPage.recipe.yield,
        aggregateRating: {
          ratingValue: 'TBD'
        },
        nutrition: {
          description: 'TBD'
        },
        author: {
          name: thuisgekooktName,
          url: thuisgekooktUrl
        }
      })
    )
  }

  return schemas
}
